import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import { Plus } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select'
import { QuestionToolTip } from '@/components/ui/question-tooltip'
import { RuleCard } from './rule-card'
import { Rule, PromotionFormData } from './promotion-form'
import { cn } from '@/lib/utils'

type RuleType = 'ContainsProduct' | 'OrderAmountMinimum' | 'OrderMinQuantity' | 'OrderMaxQuantity' | 'MaxProductQuantity' | 'PaymentIntegrationSelected' | 'DoNotContainRecurringProduct' | 'MaxAmount' | 'ExcludeProduct'

interface PromotionRulesSectionProps {
    storeId: string
    onAddRule: (type: RuleType) => void
    onUpdateRule: (index: number, updates: Partial<Rule>) => void
    onRemoveRule: (index: number) => void
}

const RuleOperatorToggle = () => {
    const { t } = useTranslation('stores')
    const { watch, setValue } = useFormContext<PromotionFormData>()
    const ruleOperator = watch('ruleOperator')

    const handleSelect = (value: 'OR' | 'AND') => {
        setValue('ruleOperator', value, { shouldDirty: true })
    }

    return (
        <div>
            <div className="mb-3 flex items-center gap-1.5">
                <p className="text-sm font-medium">{t('promotion.rules.operatorLabel')}</p>
                <QuestionToolTip title={t('promotion.rules.operatorHelp.title')}>
                    <div className="space-y-4">
                        <p className="text-muted-foreground">{t('promotion.rules.operatorHelp.intro')}</p>
                        <div className="space-y-3">
                            <div>
                                <p className="font-medium">{t('promotion.rules.operatorAnd')}</p>
                                <p className="text-muted-foreground text-xs">
                                    {t('promotion.rules.operatorHelp.andExplain')}
                                </p>
                                <p className="text-muted-foreground mt-1 text-xs italic">
                                    {t('promotion.rules.operatorHelp.andExample')}
                                </p>
                            </div>
                            <div>
                                <p className="font-medium">{t('promotion.rules.operatorOr')}</p>
                                <p className="text-muted-foreground text-xs">
                                    {t('promotion.rules.operatorHelp.orExplain')}
                                </p>
                                <p className="text-muted-foreground mt-1 text-xs italic">
                                    {t('promotion.rules.operatorHelp.orExample')}
                                </p>
                            </div>
                        </div>
                        <p className="text-foreground bg-muted rounded-md p-2 text-xs">
                            {t('promotion.rules.operatorHelp.tip')}
                        </p>
                    </div>
                </QuestionToolTip>
            </div>
            <div className="flex gap-2">
                <button
                    type="button"
                    onClick={() => handleSelect('AND')}
                    className={cn(
                        'flex-1 rounded-md border-2 px-4 py-3 text-left transition-all',
                        ruleOperator === 'AND'
                            ? 'border-primary bg-primary/10 ring-primary/20 ring-2'
                            : 'border-border hover:border-muted-foreground/30 bg-background',
                    )}
                >
                    <span className="text-sm font-semibold">{t('promotion.rules.operatorAnd')}</span>
                    <p className="text-muted-foreground mt-1 text-xs">{t('promotion.rules.operatorAndDescription')}</p>
                </button>
                <button
                    type="button"
                    onClick={() => handleSelect('OR')}
                    className={cn(
                        'flex-1 rounded-md border-2 px-4 py-3 text-left transition-all',
                        ruleOperator === 'OR'
                            ? 'border-primary bg-primary/10 ring-primary/20 ring-2'
                            : 'border-border hover:border-muted-foreground/30 bg-background',
                    )}
                >
                    <span className="text-sm font-semibold">{t('promotion.rules.operatorOr')}</span>
                    <p className="text-muted-foreground mt-1 text-xs">{t('promotion.rules.operatorOrDescription')}</p>
                </button>
            </div>
        </div>
    )
}

export const PromotionRulesSection = ({
    storeId,
    onAddRule,
    onUpdateRule,
    onRemoveRule,
}: PromotionRulesSectionProps) => {
    const { t } = useTranslation('stores')
    const { watch } = useFormContext<PromotionFormData>()
    const rules = watch('rules')

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div>
                        <div className="flex items-center gap-1.5">
                            <CardTitle>{t('promotion.rules.title')}</CardTitle>
                            <QuestionToolTip title={t('promotion.rules.titleHelp.title')}>
                                <div className="space-y-4">
                                    <p className="text-muted-foreground">{t('promotion.rules.titleHelp.intro')}</p>
                                    <div>
                                        <p className="font-medium">{t('promotion.rules.titleHelp.rulesTitle')}</p>
                                        <p className="text-muted-foreground text-xs">
                                            {t('promotion.rules.titleHelp.rulesExplain')}
                                        </p>
                                    </div>
                                    <div className="space-y-3">
                                        <p className="font-medium">{t('promotion.rules.titleHelp.examplesTitle')}</p>
                                        <div>
                                            <p className="text-xs font-medium">
                                                {t('promotion.rules.titleHelp.andExampleTitle')}
                                            </p>
                                            <p className="text-muted-foreground mt-1 text-xs italic">
                                                {t('promotion.rules.titleHelp.andExample1')}
                                            </p>
                                            <p className="text-muted-foreground mt-1 text-xs italic">
                                                {t('promotion.rules.titleHelp.andExample2')}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium">
                                                {t('promotion.rules.titleHelp.orExampleTitle')}
                                            </p>
                                            <p className="text-muted-foreground mt-1 text-xs italic">
                                                {t('promotion.rules.titleHelp.orExample1')}
                                            </p>
                                            <p className="text-muted-foreground mt-1 text-xs italic">
                                                {t('promotion.rules.titleHelp.orExample2')}
                                            </p>
                                        </div>
                                    </div>
                                    <p className="text-foreground bg-muted rounded-md p-2 text-xs">
                                        {t('promotion.rules.titleHelp.tip')}
                                    </p>
                                </div>
                            </QuestionToolTip>
                        </div>
                        <p className="text-muted-foreground mt-1 text-sm">{t('promotion.rules.description')}</p>
                    </div>
                    <Select key={rules.length} onValueChange={(value) => onAddRule(value as RuleType)}>
                        <SelectTrigger className="w-[180px]">
                            <Plus className="mr-1 h-3 w-3" />
                            {t('promotion.rules.addRule')}
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="ContainsProduct">{t('promotion.rules.titles.ContainsProduct')}</SelectItem>
                            <SelectItem value="OrderAmountMinimum">{t('promotion.rules.titles.OrderAmountMinimum')}</SelectItem>
                            <SelectItem value="OrderMinQuantity">{t('promotion.rules.titles.OrderMinQuantity')}</SelectItem>
                            <SelectItem value="OrderMaxQuantity">{t('promotion.rules.titles.OrderMaxQuantity')}</SelectItem>
                            <SelectItem value="MaxProductQuantity">{t('promotion.rules.titles.MaxProductQuantity')}</SelectItem>
                            <SelectItem value="PaymentIntegrationSelected">{t('promotion.rules.titles.PaymentIntegrationSelected')}</SelectItem>
                            <SelectItem value="DoNotContainRecurringProduct">{t('promotion.rules.titles.DoNotContainRecurringProduct')}</SelectItem>
                            <SelectItem value="MaxAmount">{t('promotion.rules.titles.MaxAmount')}</SelectItem>
                            <SelectItem value="ExcludeProduct">{t('promotion.rules.titles.ExcludeProduct')}</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </CardHeader>
            <CardContent className="space-y-3">
                <RuleOperatorToggle />
                {rules.length === 0 && (
                    <p className="text-muted-foreground py-8 text-center text-sm">{t('promotion.rules.noRules')}</p>
                )}
                {rules.map((rule, index) => (
                    <RuleCard
                        key={index}
                        rule={rule}
                        index={index}
                        storeId={storeId}
                        onUpdate={onUpdateRule}
                        onRemove={onRemoveRule}
                    />
                ))}
            </CardContent>
        </Card>
    )
}

